import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataService } from "@/services/data";
import { ApprenticeshipAppointmentType } from "@/types/apprenticeship/enterprise";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const outcome_mapping: {
    code: ApprenticeshipAppointmentType["outcome"];
    label: string;
}[] = [
    {
        code: "Ready",
        label: "Mon candidat est prêt"
    },
    {
        code: "NoShow",
        label: "Mon candidat n'est pas venu"
    },
    {
        code: "NeedsAnother",
        label: "Mon candidat a besoin d'un autre coaching"
    }
];

export function AppointmentOutcomeDialog({ appointment, size = 20 }: { appointment: ApprenticeshipAppointmentType; size?: number }) {
    const [isOpen, setIsOpen] = useState(false);
    const [outcome, setOutcome] = useState<ApprenticeshipAppointmentType["outcome"]>(appointment?.outcome);
    const navigate = useNavigate();

    if (!["coaching_interview_preparation"].includes(appointment.event_type)) return null;

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger
                asChild
                onClick={() => {
                    setIsOpen(true);
                }}
            >
                <div className={`flex cursor-pointer gap-1.5 ${appointment.outcome ? undefined : "justify-center"}`}>
                    <p className="text-xs"> {appointment.outcome ? outcome_mapping.find(om => om.code == appointment.outcome)?.label : ""}</p>
                    <Pencil width={size} height={size} cursor="pointer" className="flex-shrink-0" />
                </div>
            </DialogTrigger>
            <DialogContent className={"max-h-dvh w-full max-w-screen-sm overflow-auto"}>
                <DialogHeader>
                    <DialogTitle>Modifier le résultat de l'entretien</DialogTitle>
                </DialogHeader>

                <Label htmlFor="outcome">Résultat</Label>
                <Select
                    value={outcome ?? ""}
                    onValueChange={(e: string) => {
                        const outcomeValue = e as ApprenticeshipAppointmentType["outcome"];
                        setOutcome(outcomeValue);
                    }}
                >
                    <SelectTrigger className={outcome ? "capitalize" : "text-muted-foreground"}>
                        <SelectValue placeholder="Sélectionner le nouveau statut" />
                    </SelectTrigger>

                    <SelectContent>
                        {outcome_mapping.map((om, index) => (
                            <SelectItem value={om.code ?? ""} key={index}>
                                {om.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setIsOpen(false)}>
                        Annuler
                    </Button>
                    <Button
                        type="button"
                        onClick={() => {
                            DataService.client
                                .url(`/apprenticeship/appointments/${appointment.id}/`)
                                .patch({
                                    outcome
                                })
                                .json()
                                .then(() => {
                                    setIsOpen(false);
                                    toast.success("Résultat modifié avec succès");
                                    navigate(".");
                                })
                                .catch(() => {
                                    toast.error("Erreur lors de la modification du résultat");
                                })
                                .finally(() => {
                                    setIsOpen(false);
                                });
                        }}
                    >
                        Enregistrer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
